import { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { API, api } from "@/lib/api";
import { useApiQuery } from "@/lib/queries";
import { useAuth } from "@/lib/AuthContext";
import { Header } from "@/components/layout/Header";
import { isEmbedded } from "@/lib/embed";
import { PeriodSelector } from "@/components/shared/PeriodSelector";
import { DemoTour } from "@/components/dashboard/DemoTour";
import {
  BarChart3, Gauge, Users, Car,
  ShieldCheck, AlertTriangle, RefreshCw
} from "lucide-react";

import { OverviewTab } from "@/components/dashboard/tabs/OverviewTab";
import { AnalyseFlotteTab } from "@/components/dashboard/tabs/AnalyseFlotteTab";
import { DriversTab } from "@/components/dashboard/tabs/DriversTab";
import { VehiclesTab } from "@/components/dashboard/tabs/VehiclesTab";
import { AuditTab } from "@/components/dashboard/tabs/AuditTab";

const fmt = (d) => d.toISOString().split('T')[0];
const initFrom = () => { const d = new Date(); d.setDate(d.getDate() - 6); return fmt(d); };

const TABS = [
  { id: "overview", label: "Vue generale", icon: BarChart3 },
  { id: "analyse", label: "Analyse flotte", icon: Gauge },
  { id: "drivers", label: "Conducteurs", icon: Users },
  { id: "vehicles", label: "Vehicules", icon: Car },
];
const ADMIN_TAB = { id: "audit", label: "Audit", icon: ShieldCheck };

// Squelette progressif — remplace l'ancien spinner bloquant plein écran
const TabSkeleton = () => (
  <div className="p-4 lg:p-8 max-w-[1600px] mx-auto space-y-5" data-testid="tab-skeleton">
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse">
          <div className="h-3 w-16 bg-gray-100 rounded mb-3" />
          <div className="h-7 w-20 bg-gray-200 rounded" />
        </div>
      ))}
    </div>
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="bg-white border border-gray-200 rounded-xl h-64 animate-pulse" />
      <div className="bg-white border border-gray-200 rounded-xl h-64 animate-pulse" />
    </div>
    <div className="bg-white border border-gray-200 rounded-xl h-48 animate-pulse" />
  </div>
);

// Erreur localisée à l'onglet (les autres onglets restent accessibles) — jamais masquée par un cache
const TabError = ({ onRetry }) => (
  <div className="p-8 max-w-[1600px] mx-auto" data-testid="tab-error">
    <div className="p-6 bg-red-50 border border-red-100 rounded-xl text-sm text-red-700 flex flex-wrap items-center justify-between gap-3">
      <span className="flex items-center gap-2">
        <AlertTriangle size={16} className="shrink-0" />
        Impossible de charger les données de cet onglet (source LOGITRAK en erreur).
      </span>
      <button onClick={onRetry} data-testid="tab-error-retry"
        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-red-200 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors">
        <RefreshCw size={12} />Réessayer
      </button>
    </div>
  </div>
);

export const DashboardLayout = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [vehicleToOpen, setVehicleToOpen] = useState(null);
  const [period, setPeriod] = useState("week");
  const [fromDate, setFromDate] = useState(initFrom);
  const [toDate, setToDate] = useState(() => fmt(new Date()));
  // Période APPLIQUÉE = clés des requêtes (les inputs custom ne déclenchent rien avant OK)
  const [applied, setApplied] = useState(() => ({ from: initFrom(), to: fmt(new Date()) }));
  const [debugMode, setDebugMode] = useState(false);
  const { user, actAs } = useAuth();
  const isAdmin = user?.role === "SUPER_ADMIN";
  const queryClient = useQueryClient();

  const params = { from_date: applied.from, to_date: applied.to };

  // Requêtes PARTAGÉES entre onglets (clé = tenant + chemin + params, dédup in-flight)
  // trends / idle-by-group : non affichés → plus chargés ; vehicle-comparison : à la demande (drill-down Distance)
  // Auto-refresh 2 min sur les seules requêtes vivantes (pause auto si fenêtre en arrière-plan —
  // React Query natif) ; les recomputes coûteux restent bornés par les TTL du cache backend
  const ctxQ = useApiQuery('/tenant/context');
  const statsQ = useApiQuery('/fleet/stats', params, { refetchInterval: 120_000 });
  const effQ = useApiQuery('/fleet/efficiency', params, { refetchInterval: 120_000 });
  useApiQuery('/groups'); // préchauffage — clé partagée avec Analyse flotte / Véhicules (1er passage instantané)

  const modules = ctxQ.data?.success ? ctxQ.data.modules : null;
  const MODULE_BY_TAB = { overview: "dashboard", analyse: "analyse", drivers: "conducteurs", vehicles: "vehicules" };
  const baseTabs = modules ? TABS.filter((t) => modules.includes(MODULE_BY_TAB[t.id])) : TABS;
  const visibleTabs = isAdmin && !actAs ? [...baseTabs, ADMIN_TAB] : baseTabs;

  useEffect(() => {
    if (modules && visibleTabs.length && !visibleTabs.some((t) => t.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [modules]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = useMemo(() => ({
    stats: statsQ.data?.success ? statsQ.data : null,
    efficiency: effQ.data?.success ? effQ.data : null,
  }), [statsQ.data, effQ.data]);

  // Requêtes requises PAR ONGLET → skeleton/erreur localisés, pas de blocage global
  const NEEDS = {
    overview: [statsQ, effQ],
    analyse: [statsQ, effQ],
    drivers: [],
    vehicles: [statsQ, effQ],
    audit: [],
  };
  const needed = NEEDS[activeTab] || [];
  const tabLoading = needed.some((q) => q.isPending);
  const tabError = !tabLoading && needed.some((q) => q.isError || q.data?.success === false);
  const retryTab = () => needed.forEach((q) => { if (q.isError || q.data?.success === false) q.refetch(); });

  // Âge HONNÊTE des données : heure de fetch client MOINS l'âge du cache backend (_audit.cache.age_seconds)
  const backendAgeMs = (statsQ.data?._audit?.cache?.age_seconds || 0) * 1000;
  const lastUpdate = statsQ.dataUpdatedAt ? new Date(statsQ.dataUpdatedAt - backendAgeMs).toISOString() : null;
  // Refresh manuel : invalide TOUTES les requêtes du cache client (refetch immédiat des actives)
  const handleRefresh = () => queryClient.invalidateQueries();

  const handlePeriodApply = (from, to) => setApplied({ from, to });

  const handleExportPDF = async () => {
    try {
      const res = await api.get(`${API}/export/pdf`, {
        params: { from_date: applied.from, to_date: applied.to },
        responseType: 'blob',
        timeout: 120000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_flotte_${applied.from}_${applied.to}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("PDF export failed:", e);
    }
  };

  const handleTabNavigate = (tabId) => setActiveTab(tabId);

  const vehicles = data.stats?.vehicles || [];
  const activeVehicles = vehicles.filter(v => v.connection_status === "active").length;
  const totalKm = (data.stats?.summary?.total_mileage || 0).toFixed(0);

  return (
    <div data-testid="dashboard-layout">
      <Header title="Dashboard"
        subtitle={`${vehicles.length} vehicules — ${activeVehicles} en ligne — ${totalKm} km`}
        onRefresh={handleRefresh} lastUpdate={lastUpdate} alertCount={0}
        debugMode={debugMode} onDebugToggle={() => setDebugMode(d => !d)}
        onExportPDF={!modules || modules.includes("rapports") ? handleExportPDF : undefined}>
        <PeriodSelector period={period} setPeriod={setPeriod}
          fromDate={fromDate} setFromDate={setFromDate} toDate={toDate} setToDate={setToDate}
          onApply={handlePeriodApply} />
      </Header>

      <div className={`sticky ${isEmbedded ? "top-12" : "top-16"} z-[60] bg-white border-b border-gray-200`} data-testid="dashboard-tabs">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
          <div className="flex items-center gap-0 overflow-x-auto scrollbar-hide -mb-px">
            {visibleTabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 ${isEmbedded ? "py-2" : "py-3.5"} text-[13px] font-medium whitespace-nowrap border-b-2 transition-all duration-200
                    ${isActive
                      ? tab.id === 'audit' ? "border-red-500 text-red-600" : "border-[#111] text-[#111]"
                      : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300"}`}>
                  <tab.icon size={15} className={isActive ? (tab.id === 'audit' ? "text-red-500" : "text-[#111]") : "text-gray-400"} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {(() => {
        const ds = { ...(data.stats?.data_status || {}), ...(data.efficiency?.data_status || {}) };
        const issues = Object.entries(ds).filter(([, s]) => s !== "ok");
        if (!issues.length || tabLoading) return null;
        return (
          <div className="bg-amber-50 border-b border-amber-200" data-testid="partial-data-banner">
            <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-2 text-xs text-amber-800 flex items-center gap-1.5">
              <AlertTriangle size={13} className="shrink-0" /> <span>Données partielles — sources en {issues.some(([, s]) => s === "error") ? "erreur" : "réponse incomplète"} :{" "}
              {issues.map(([k, s]) => `${k} (${s === "error" ? "échec" : "partiel"})`).join(", ")}. Les totaux affichés peuvent être incomplets.</span>
            </div>
          </div>
        );
      })()}

      <div className="transition-opacity duration-200">
        {tabLoading ? (
          <TabSkeleton />
        ) : tabError ? (
          <TabError onRetry={retryTab} />
        ) : (
          <>
            {activeTab === "overview" && <OverviewTab data={data} debugMode={debugMode} fromDate={applied.from} toDate={applied.to} onNavigate={handleTabNavigate}
              onOpenVehicle={(tid) => { setVehicleToOpen(tid); setActiveTab("vehicles"); }} />}
            {activeTab === "analyse" && <AnalyseFlotteTab data={data} fromDate={applied.from} toDate={applied.to} debugMode={debugMode} />}
            {activeTab === "drivers" && <DriversTab data={data} fromDate={applied.from} toDate={applied.to} debugMode={debugMode} />}
            {activeTab === "vehicles" && <VehiclesTab data={data} debugMode={debugMode} initialSelected={vehicleToOpen} onConsumedInitial={() => setVehicleToOpen(null)} />}
            {activeTab === "audit" && <AuditTab fromDate={applied.from} toDate={applied.to} />}
          </>
        )}
      </div>

      {(user?.tenant_id === "demo-ev" || actAs?.tenant === "demo-ev") && <DemoTour />}
    </div>
  );
};
